import { useEffect, useRef, useState, useCallback } from "react";
import { useAppSelector } from "../../store/hooks";
import { useGetQuery } from "../../store/slices/apiSlice";
import { useLogObserver } from "../../hooks/use-logObserver";
import { Log } from "../../store/types";

interface LogDataLoaderProps {
  log: Log;
  wellUid: string;
  wellboreUid: string;
  onDataLoaded: (logUid: string, data: any) => void;
  onDataUpdate: (logUid: string, newPoint: any) => void;
  onError: (logUid: string) => void;
}

const LogDataLoader: React.FC<LogDataLoaderProps> = ({
  log,
  wellUid,
  wellboreUid,
  onDataLoaded,
  onDataUpdate,
  onError,
}) => {
  const logsEP = useAppSelector((state) => state.endpoints.logs);
  const wellsEP = useAppSelector((state) => state.endpoints.wells);
  const wellboresEP = useAppSelector((state) => state.endpoints.wellbores);

  const url = `${wellsEP}/${wellUid}${wellboresEP}/${wellboreUid}${logsEP}/${log.uid}/data`;
  const { data, isLoading, error } = useGetQuery(url, {
    skip:
      !wellsEP ||
      !wellUid ||
      !wellboresEP ||
      !wellboreUid ||
      !logsEP ||
      !log.uid,
    refetchOnMountOrArgChange: true,
  });

  const { subscribe } = useLogObserver();
  const hasNotified = useRef(false);

  useEffect(() => {
    if (hasNotified.current) return;
    if (!isLoading && data !== undefined) {
      hasNotified.current = true;
      onDataLoaded(log.uid, data);
    }
    if (error) {
      hasNotified.current = true;
      onError(log.uid);
    }
  }, [data, isLoading, error, log.uid, onDataLoaded, onError]);

  useEffect(() => {
    const unsubscribe = subscribe(log.uid, (newPoint) => {
      onDataUpdate(log.uid, newPoint);
    });
    return unsubscribe;
  }, [log.uid, subscribe, onDataUpdate]);

  return null;
};

export default LogDataLoader;
